import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { X, Plus, CheckCircle2, Circle, Flame, Droplets, Snowflake } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEAD_STATUS_OPTIONS = [
  { value: 'hot', label: 'Hot Lead', icon: Flame, color: 'text-red-600', bgColor: 'bg-red-100', description: 'High interest, ready to move' },
  { value: 'warm', label: 'Warm Lead', icon: Droplets, color: 'text-orange-600', bgColor: 'bg-orange-100', description: 'Interested, needs nurturing' },
  { value: 'cold', label: 'Cold Lead', icon: Snowflake, color: 'text-blue-600', bgColor: 'bg-blue-100', description: 'Low interest or not ready' }
];

const LeadManagementModal = ({ visitor, isOpen, onClose, onUpdate, token }) => {
  const [leadStatus, setLeadStatus] = useState(visitor?.lead_status || '');
  const [notes, setNotes] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newReminder, setNewReminder] = useState({ text: '', due_date: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visitor && isOpen) {
      fetchNotesAndReminders();
      setLeadStatus(visitor.lead_status || '');
    }
  }, [visitor, isOpen]);

  const fetchNotesAndReminders = async () => {
    try {
      const visitorId = visitor.master_visitor_id || visitor.id;
      const [notesRes, remindersRes] = await Promise.all([
        axios.get(`${API}/visitors/${visitorId}/notes`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/visitors/${visitorId}/reminders`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setNotes(notesRes.data);
      setReminders(remindersRes.data);
    } catch (error) {
      console.error('Failed to fetch notes/reminders');
    }
  };

  const handleStatusUpdate = async (status) => {
    try {
      const visitorId = visitor.master_visitor_id || visitor.id;
      await axios.patch(
        `${API}/visitors/${visitorId}/status`,
        { lead_status: status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeadStatus(status);
      toast.success('Lead status updated');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setLoading(true);
    try {
      const visitorId = visitor.master_visitor_id || visitor.id;
      const response = await axios.post(
        `${API}/visitors/${visitorId}/notes`,
        { text: newNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotes([...notes, response.data]);
      setNewNote('');
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setLoading(false);
    }
  };

  const handleAddReminder = async () => {
    if (!newReminder.text.trim() || !newReminder.due_date) return;
    
    setLoading(true);
    try {
      const visitorId = visitor.master_visitor_id || visitor.id;
      const response = await axios.post(
        `${API}/visitors/${visitorId}/reminders`,
        newReminder,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReminders([...reminders, response.data]);
      setNewReminder({ text: '', due_date: '' });
      toast.success('Reminder added');
    } catch (error) {
      toast.error('Failed to add reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteReminder = async (reminderId) => {
    try {
      const visitorId = visitor.master_visitor_id || visitor.id;
      await axios.patch(
        `${API}/visitors/${visitorId}/reminders/${reminderId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReminders(reminders.map(r => 
        r.id === reminderId ? { ...r, completed: true } : r
      ));
      toast.success('Reminder completed');
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };

  if (!visitor) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>
            Manage Lead: {visitor.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Lead Status */}
          <div>
            <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-3 block">Lead Status</Label>
            <div className="grid grid-cols-3 gap-3">
              {LEAD_STATUS_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = leadStatus === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleStatusUpdate(option.value)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      isSelected
                        ? `${option.bgColor} border-current ${option.color}`
                        : 'border-stone-200 hover:border-stone-300 text-stone-600'
                    }`}
                    data-testid={`status-${option.value}`}
                  >
                    <Icon className="mx-auto mb-2" size={24} />
                    <p className="font-semibold text-sm">{option.label}</p>
                    <p className="text-xs mt-1 opacity-75">{option.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-3 block">Agent Notes</Label>
            <div className="space-y-3 mb-3">
              {notes.map((note) => (
                <Card key={note.id} className="p-4 bg-stone-50">
                  <p className="text-sm text-stone-700">{note.text}</p>
                  <p className="text-xs text-stone-500 mt-2">
                    {note.created_by} - {new Date(note.created_at).toLocaleString()}
                  </p>
                </Card>
              ))}
            </div>
            <div className="flex gap-2">
              <Textarea
                data-testid="new-note-input"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note about this lead..."
                className="flex-1 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] rounded-lg"
              />
              <Button
                data-testid="add-note-button"
                onClick={handleAddNote}
                disabled={loading || !newNote.trim()}
                className="bg-[#1A4D2E] text-white rounded-full px-6 hover:bg-[#143D24]"
              >
                <Plus size={16} className="mr-1" />
                Add
              </Button>
            </div>
          </div>

          {/* Reminders */}
          <div>
            <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-3 block">Follow-up Reminders</Label>
            <div className="space-y-2 mb-3">
              {reminders.map((reminder) => (
                <Card key={reminder.id} className={`p-4 flex items-start gap-3 ${reminder.completed ? 'bg-green-50' : 'bg-stone-50'}`}>
                  <button
                    onClick={() => !reminder.completed && handleCompleteReminder(reminder.id)}
                    className="mt-1"
                  >
                    {reminder.completed ? (
                      <CheckCircle2 className="text-green-600" size={20} />
                    ) : (
                      <Circle className="text-stone-400" size={20} />
                    )}
                  </button>
                  <div className="flex-1">
                    <p className={`text-sm ${reminder.completed ? 'line-through text-stone-500' : 'text-stone-700'}`}>
                      {reminder.text}
                    </p>
                    <p className="text-xs text-stone-500 mt-1">
                      Due: {new Date(reminder.due_date).toLocaleDateString()}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
            <div className="space-y-3">
              <Input
                data-testid="new-reminder-text"
                value={newReminder.text}
                onChange={(e) => setNewReminder({ ...newReminder, text: e.target.value })}
                placeholder="Reminder text..."
                className="bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] rounded-lg"
              />
              <div className="flex gap-2">
                <Input
                  data-testid="new-reminder-date"
                  type="date"
                  value={newReminder.due_date}
                  onChange={(e) => setNewReminder({ ...newReminder, due_date: e.target.value })}
                  className="flex-1 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] rounded-lg"
                />
                <Button
                  data-testid="add-reminder-button"
                  onClick={handleAddReminder}
                  disabled={loading || !newReminder.text.trim() || !newReminder.due_date}
                  className="bg-[#D4A373] text-white rounded-full px-6 hover:bg-[#C89563]"
                >
                  <Plus size={16} className="mr-1" />
                  Add Reminder
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LeadManagementModal;
